export class SmartItem {
  key = ""; // 唯一标识
  value = ""; // 显示值
  rawData = null; // 原始数据
  constructor(options = {}) {
    if (options) {
      Object.assign(this, options);
    }
  }
}

export const SmartControlMixin = {
  /**
   * select 是完整的SmartItem
   */
  emits: ["input"],
  props: {
    loadMethod: { type: Function, required: false }, // 远程请求方法
    options: { type: Array, default: null }, // 本地静态数据
    value: null, // v-model
    multiple: { type: Boolean, default: false },
    pageSize: { type: Number, default: 20 },
    pageable: { type: Boolean, default: false },
    autoLoad: { type: Boolean, default: true },
    fetchParams: { type: Object, default: () => ({}) },
    queryParameters: { type: Object, default: () => ({}) }, // 新增，统一查询条件
    likeFields: { type: Array, default: () => [] }, // 新增，哪些字段用like %% 查询
    autoSelect: { type: Boolean, default: true },
    addAllOption: { type: [Boolean, Object], default: false },
    displayField: { type: String, default: "name" }, // 新增
    valueField: { type: String, default: "id" }, // 新增
    valueType: { type: String, default: "auto" }, // 'auto'|'number' | 'string' | 'boolean' | 'object'
    responseListField: { type: String, default: "list" }, // 新增，默认 "list"
  },
  data() {
    return {
      loading: false,
      list: [], // 当前显示的（过滤后）
      allList: [], // 全量数据
      selected: this.multiple ? [] : null,
      page: 1,
      total: 0,
    };
  },
  watch: {
    fetchParams: {
      handler() {
        this.loadList();
      },
      deep: true,
    },
    // queryParameters watcher 已合并到下方，避免重复
    options: {
      handler() {
        const me = this;
        if (me.options) {
          me.allList = me.modelValueToItem(me.options);
          me.initializeSelectedValue();
        }
      },
      immediate: true,
    },
    queryParameters: {
      handler() {
        // 离线数据时本地过滤，分页接口时远程过滤
        if (this.isOfflineMode()) {
          this.applyLocalFilter();
        } else {
          this.loadList();
        }
      },
      deep: true,
    },
    value: {
      handler(val) {
        this.selected = val;
      },
      immediate: true,
    },
  },
  methods: {
    // v-model值转key或key数组，支持多种 valueType
    modelToKeys(value) {
      const { valueType, valueField } = this;
      const vt = valueType ? valueType.toLowerCase() : "";
      if (vt.startsWith("array<")) {
        // 多选
        if (!Array.isArray(value)) return [];
        const innerType = vt.slice(6, -1);
        if (innerType === "object") {
          // Array<Object> => key数组
          const keys = value.map((v) =>
            v && typeof v === "object" && valueField in v ? v[valueField] : v
          );
          return keys;
        } else {
          // Array<primitive> => 直接返回
          return value.map((v) => this.castValueType(v));
        }
      } else {
        // 单选
        if (vt === "object") {
          return value && typeof value === "object" && valueField in value
            ? value[valueField]
            : value;
        } else {
          return this.castValueType(value);
        }
      }
    },

    // key或key数组转对象或对象数组，支持多种 valueType
    keysToModel(keys) {
      console.log(keys);
      const { valueType, value, valueField } = this;
      const vt = valueType ? valueType.toLowerCase() : "";
      if (vt.toLowerCase().startsWith("array<")) {
        // 多选
        if (!Array.isArray(keys)) return [];
        const innerType = vt.slice(6, -1);
        if (innerType === "object") {
          // key数组 => 对象数组
          return keys.map((k) => {
            const found = (this.list || []).find((item) => item.key == k);
            return found
              ? found.rawData
              : Array.isArray(value)
              ? value.find((item) => item[valueField] == k)
              : {}; //如果下拉项目中没有直接使用props.value传入的列表项目
          });
        } else {
          // key数组 => primitive数组
          return keys.map((k) => this.castValueType(k));
        }
      } else {
        // 单选
        if (vt === "object") {
          const found = (this.list || []).find((item) => item.key == keys);
          return found ? found.rawData : value || {}; //下拉项目中没有则使用props.value
        } else {
          return this.castValueType(keys);
        }
      }
    },
    async loadList() {
      const me = this;
      // 离线模式直接本地过滤
      if (me.isOfflineMode()) {
        me.applyLocalFilter();
        return;
      }
      if (!me.loadMethod) return;
      me.loading = true;
      try {
        // 合并 fetchParams 和 queryParameters
        let params = { ...me.fetchParams, ...me.queryParameters };
        // 支持 likeFields 字段自动加 %%
        if (me.likeFields && Array.isArray(me.likeFields)) {
          me.likeFields.forEach((field) => {
            if (
              params[field] &&
              typeof params[field] === "string" &&
              !params[field].startsWith("%")
            ) {
              params[field] = `%${params[field]}%`;
            }
          });
        }
        const res = await me.loadMethod({
          ...params,
          page: me.page,
          pageSize: me.pageSize,
        });
        // 自动兼容不同的列表字段
        let dataList = [];
        if (Array.isArray(res)) {
          dataList = res; // 如果服务器直接返回数组
        } else if (Array.isArray(res[me.listField])) {
          dataList = res[me.listField];
        } else {
          // 尝试自动查找第一个数组字段
          for (const key in res) {
            if (Array.isArray(res[key])) {
              dataList = res[key];
              break;
            }
          }
        }
        // 判断是否为离线数据
        if (
          Array.isArray(dataList) &&
          !res.total &&
          !res.page &&
          !res.pageSize
        ) {
          me.allList = me.modelValueToItem(dataList);
          me.applyLocalFilter();
        } else {
          me.list = me.modelValueToItem(dataList);
          me.total = res.total || dataList.length;
          me.initializeSelectedValue();
        }
      } catch (e) {
        me.$emit("error", e);
      } finally {
        me.loading = false;
      }
    },

    isOfflineMode() {
      // options存在，或loadMethod返回数组且无total/page等分页信息
      if (this.options) return true;
      if (this.allList && this.allList.length) return true;
      return false;
    },

    applyLocalFilter() {
      // 本地过滤 allList，支持 queryParameters 和 likeFields
      const me = this;
      let filtered = (me.allList || []).slice();
      const filters = { ...me.queryParameters };
      const likeFields = me.likeFields || [];
      filtered = filtered.filter((item) => {
        return Object.keys(filters).every((key) => {
          const value = filters[key];
          if (
            value === undefined ||
            value === null ||
            value === "" ||
            (value instanceof Array && value.length === 0)
          )
            return true;
          let fieldValue = item.rawData ? item.rawData[key] : item[key];
          if (typeof value === "string" && likeFields.includes(key)) {
            const filterStr = value.replace(/%/g, "").toLowerCase();
            return String(fieldValue ?? "")
              .toLowerCase()
              .includes(filterStr);
          } else {
            return fieldValue === value;
          }
        });
      });
      me.list = filtered;
      me.total = filtered.length;
      me.initializeSelectedValue();
    },
    castValueType(value) {
      const me = this;
      if (value == null) return value; // 处理 null 和 undefined
      if (value == undefined) return value; // 处理 undefined

      if (me.valueType === "number") {
        return Number(value);
      } else if (me.valueType === "string") {
        return String(value);
      } else if (me.valueType === "boolean") {
        return Boolean(value);
      } else if (me.valueType === "object") {
        return value; // 对象类型直接返回
      } else {
        return value;
      }
    },
    handleSelect(item) {
      console.log(item);
      const me = this;
      let value;
      if (me.valueType === "object") {
        value = item.rawData || item;
      } else {
        value = item.key !== undefined ? item.key : item[me.valueField];
        value = me.castValueType(value);
      }
      if (me.multiple) {
        if (!Array.isArray(me.selected)) me.selected = [];
        const isEqual = (a, b) => {
          if (me.valueType === "object") {
            if (me.valueField && a && b) {
              return a[me.valueField] === b[me.valueField];
            }
            return JSON.stringify(a) === JSON.stringify(b);
          }
          return a === b;
        };
        const idx = me.selected.findIndex((i) => isEqual(i, value));
        if (idx > -1) {
          me.selected.splice(idx, 1);
        } else {
          me.selected.push(value);
        }
      } else {
        me.selected = value;
      }
      me.$emit("input", me.selected);
    },
    handlePageChange(page) {
      this.page = page;
      this.loadList();
    },
    handleClear() {
      this.selected = this.multiple ? [] : null;
      this.$emit("input", this.selected);
    },

    autoSelectFirstItem() {
      const me = this;
      if (!Array.isArray(me.list) || me.list.length === 0) return;
      if (me.valueType === "object") {
        return me.multiple ? [me.list[0].rawData] : me.list[0].rawData;
      } else {
        return me.multiple
          ? [me.castValueType(me.list[0].key)]
          : me.castValueType(me.list[0].key);
      }
    },

    initializeSelectedValue() {
      const me = this;
      // 只在未选中时自动选中
      if (
        me.autoSelect &&
        (me.selected == null ||
          (Array.isArray(me.selected) && !me.selected.length)) &&
        Array.isArray(me.list) &&
        me.list.length > 0
      ) {
        let value = me.value;

        if (me.multiple) {
          // 多选，value 应该是数组
          let values = Array.isArray(value) ? value : [];
          let selectedArr = values
            .map((val) => {
              if (me.valueType === "object") {
                const found = me.list.find(
                  (item) =>
                    item.rawData &&
                    val &&
                    val[me.valueField] !== undefined &&
                    item.rawData[me.valueField] == val[me.valueField]
                );
                // 修复：如果没找到则保留原对象
                return found ? found.rawData : val;
              } else {
                const found = me.list.find(
                  (item) => item.key == me.castValueType(val)
                );
                return found ? found.key : val;
              }
            })
            .filter((v) => v !== undefined && v !== null);

          // 如果没有匹配到任何项，默认选第一项
          if (!selectedArr.length) {
            selectedArr = undefined;
            // 保证多选为数组
            selectedArr = Array.isArray(selectedArr)
              ? selectedArr
              : [selectedArr];
          }
          me.selected = selectedArr;
        } else {
          // 单选
          let selectedValue;
          if (value !== undefined && value !== null) {
            if (me.valueType === "object") {
              const found = me.list.find(
                (item) =>
                  item.rawData &&
                  value[me.valueField] !== undefined &&
                  item.rawData[me.valueField] == value[me.valueField]
              );
              // 修复：如果没找到则保留原对象
              selectedValue = found ? found.rawData : value;
            } else {
              const found = me.list.find(
                (item) => item.key == me.castValueType(value)
              );
              selectedValue = found ? me.castValueType(found.key) : value;
            }
          }

          me.selected = selectedValue;
        }
        me.$emit("input", me.selected);
      }
    },

    modelValueToItem(array = []) {
      const me = this;
      let options = array.map(
        (item) =>
          new SmartItem({
            key: item[me.valueField],
            value: item[me.displayField],
            rawData: item,
          })
      );
      if (me.addAllOption) {
        if (typeof me.addAllOption === "object") {
          options.unshift(
            new SmartItem({
              key: me.addAllOption.key,
              value: me.addAllOption.value,
              rawData: null,
            })
          );
        } else {
          options.unshift(
            new SmartItem({ key: undefined, value: "all", rawData: null })
          );
        }
      }
      return options;
    },
    getDisplay(item) {
      const me = this;
      if (me.valueType === "object") {
        return item.rawData ? item.rawData[me.displayField] : "";
      } else if (["number", "string", "boolean"].includes(me.valueType)) {
        return item.key;
      } else {
        return item.rawData;
      }
    },
  },

  created() {
    const me = this;
    if (me.options instanceof Array && me.options.length > 0) {
      me.list = me.modelValueToItem(me.options);
      me.total = me.list.length;
      me.initializeSelectedValue();
    } else if (me.autoLoad) {
      me.loadList();
    }
  },
};
